<template>
  <manual-page
    title="Contabilidad"
    description="Guia completa para gestionar ingresos, egresos y el control financiero del sistema."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'accounting' }"
    action-text="Ir a contabilidad"
  >
    <!-- Overview -->
    <h2>Vista General del Modulo</h2>
    <p>
      El modulo de contabilidad centraliza toda la informacion financiera del sistema.
      Aqui puedes consultar ingresos, registrar gastos, y obtener una vision clara
      del estado financiero de cada sucursal y de la red completa.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Desde este modulo puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Ver todos los ingresos por pagos de clientes</li>
        <li>Registrar ingresos manuales adicionales</li>
        <li>Documentar gastos y egresos operativos</li>
        <li>Consultar el balance por periodo</li>
        <li>Filtrar movimientos por sucursal</li>
        <li>Generar reportes financieros</li>
      </ul>
    </div>

    <!-- Incomes Section -->
    <h2>Gestion de Ingresos</h2>
    <p>
      La seccion de ingresos muestra todo el dinero que entra al negocio,
      tanto de cobros automaticos como de registros manuales.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipos de ingresos</h3>
          <p class="text-gray-600 mb-2">El sistema registra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Automaticos:</strong> Pagos de ordenes procesados por Stripe</li>
            <li><strong>Manuales:</strong> Ingresos registrados por el administrador</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion mostrada</h3>
          <p class="text-gray-600 mb-2">Cada ingreso incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Fecha:</strong> Cuando se recibio el pago</li>
            <li><strong>Monto:</strong> Cantidad recibida</li>
            <li><strong>Concepto:</strong> Descripcion del ingreso</li>
            <li><strong>Sucursal:</strong> A que sucursal corresponde</li>
            <li><strong>Cliente:</strong> Quien realizo el pago (si aplica)</li>
            <li><strong>Metodo:</strong> Forma de pago utilizada</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Los pagos de ordenes se registran automaticamente cuando
        Stripe confirma el cobro. No necesitas hacer nada manualmente para estos ingresos.
      </p>
    </div>

    <!-- Registering Income -->
    <h3>Registrar un Ingreso Manual</h3>
    <p>
      Para documentar ingresos que no vienen de pagos automaticos (ventas en efectivo,
      transferencias directas, etc.):
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Ve a Contabilidad > Ingresos y haz clic en "Registrar".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Completar datos</h3>
          <p class="text-gray-600 mb-2">Ingresa la siguiente informacion:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Fecha:</strong> Cuando se recibio el dinero</li>
            <li><strong>Monto:</strong> Cantidad exacta recibida</li>
            <li><strong>Concepto:</strong> Descripcion clara del ingreso</li>
            <li><strong>Sucursal:</strong> A que sucursal asignar el ingreso</li>
            <li><strong>Metodo de pago:</strong> Efectivo, transferencia, etc.</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Guardar registro</h3>
          <p class="text-gray-600">
            Verifica que todos los datos esten correctos y guarda el registro.
            El ingreso aparecera inmediatamente en los reportes.
          </p>
        </div>
      </div>
    </div>

    <!-- Expenses Section -->
    <h2>Gestion de Egresos</h2>
    <p>
      La seccion de egresos te permite documentar todos los gastos del negocio.
      Un registro completo de gastos es esencial para conocer la rentabilidad real.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipos de egresos</h3>
          <p class="text-gray-600 mb-2">Los gastos comunes incluyen:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Materia prima:</strong> Compra de ingredientes</li>
            <li><strong>Nomina:</strong> Salarios del personal</li>
            <li><strong>Servicios:</strong> Luz, agua, internet, etc.</li>
            <li><strong>Renta:</strong> Alquiler de locales</li>
            <li><strong>Transporte:</strong> Gastos de entrega</li>
            <li><strong>Otros:</strong> Gastos varios</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion de cada egreso</h3>
          <p class="text-gray-600 mb-2">Cada registro incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Fecha:</strong> Cuando se realizo el gasto</li>
            <li><strong>Monto:</strong> Cantidad gastada</li>
            <li><strong>Categoria:</strong> Tipo de gasto</li>
            <li><strong>Descripcion:</strong> Detalle del gasto</li>
            <li><strong>Sucursal:</strong> A que sucursal corresponde</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Registering Expense -->
    <h3>Registrar un Egreso</h3>
    <p>Para documentar un nuevo gasto:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Ve a Contabilidad > Egresos y haz clic en "Crear".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Completar datos</h3>
          <p class="text-gray-600 mb-2">Ingresa la informacion del gasto:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Fecha:</strong> Cuando se realizo el gasto</li>
            <li><strong>Monto:</strong> Cantidad exacta</li>
            <li><strong>Categoria:</strong> Selecciona el tipo de gasto</li>
            <li><strong>Descripcion:</strong> Detalle especifico</li>
            <li><strong>Sucursal:</strong> A que sucursal asignar</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Adjuntar comprobante (opcional)</h3>
          <p class="text-gray-600">
            Si tienes factura o recibo, puedes adjuntarlo al registro
            para tener respaldo documental.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Registra todos los gastos de forma consistente.
        Un registro incompleto dara una imagen distorsionada de la rentabilidad.
      </p>
    </div>

    <!-- Filtering by Branch -->
    <h2>Filtrar por Sucursal</h2>
    <p>
      Puedes ver los movimientos financieros de una sucursal especifica
      o el consolidado de toda la red.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Usar el selector</h3>
          <p class="text-gray-600">
            En la parte superior de la seccion de contabilidad encontraras
            un selector de sucursal. Por defecto muestra "Todas".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seleccionar sucursal</h3>
          <p class="text-gray-600">
            Elige la franquicia y sucursal que deseas consultar.
            Los datos se filtraran automaticamente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver consolidado</h3>
          <p class="text-gray-600">
            Selecciona "Todas" para ver el total de todas las sucursales.
            Esto es util para reportes corporativos.
          </p>
        </div>
      </div>
    </div>

    <!-- Period Selection -->
    <h2>Seleccion de Periodo</h2>
    <p>
      Puedes consultar los movimientos de diferentes periodos de tiempo
      para analizar tendencias y comparar resultados.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Opciones de periodo:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Hoy:</strong> Movimientos del dia actual</li>
        <li><strong>Esta semana:</strong> Ultimos 7 dias</li>
        <li><strong>Este mes:</strong> Mes en curso</li>
        <li><strong>Mes anterior:</strong> Mes previo completo</li>
        <li><strong>Personalizado:</strong> Rango de fechas especifico</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa los numeros semanalmente para detectar
        problemas o tendencias a tiempo. No esperes al fin de mes.
      </p>
    </div>

    <!-- Balance -->
    <h2>Balance General</h2>
    <p>
      El balance muestra la diferencia entre ingresos y egresos del periodo
      seleccionado, dando una vision rapida de la rentabilidad.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Metricas principales</h3>
          <p class="text-gray-600 mb-2">El resumen muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Total ingresos:</strong> Suma de todos los ingresos del periodo</li>
            <li><strong>Total egresos:</strong> Suma de todos los gastos del periodo</li>
            <li><strong>Balance:</strong> Ingresos menos egresos (utilidad o perdida)</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Interpretacion</h3>
          <p class="text-gray-600">
            Un balance positivo indica utilidad en el periodo.
            Un balance negativo indica que los gastos superaron los ingresos.
          </p>
        </div>
      </div>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Practicas</h2>
    <ul>
      <li><strong>Registro diario:</strong> Documenta los gastos el mismo dia que ocurren</li>
      <li><strong>Categorizar correctamente:</strong> Usa las categorias apropiadas para cada gasto</li>
      <li><strong>Guardar comprobantes:</strong> Adjunta facturas y recibos como respaldo</li>
      <li><strong>Revisar periodicamente:</strong> Analiza los numeros al menos semanalmente</li>
      <li><strong>Comparar periodos:</strong> Compara con meses anteriores para detectar cambios</li>
      <li><strong>Asignar sucursal:</strong> Siempre indica a que sucursal corresponde cada movimiento</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Recordatorio:</strong> La contabilidad del sistema es para control interno.
        Consulta con un contador profesional para obligaciones fiscales y reportes oficiales.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualAccounting",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualReports", label: "Reportes" },
        { route: "manualOrders", label: "Ordenes" },
        { route: "manualFranchises", label: "Franquicias" },
        { route: "manualStore", label: "Tienda" },
      ],
    };
  },
};
</script>
